import PropTypes from 'prop-types'
import { useMemo, useState, useEffect } from 'react'
import { useFieldArray, useWatch } from 'react-hook-form'
import styled from 'styled-components'
import isArray from 'lodash/isArray'
import Checkbox from '../../atomics/Checkbox'
import VariantItem from '../../molecules/VariantItem/VariantItem'
import ModalTemplate from '../../templates/ModalTemplate'
import CardProduct from '../../atomics/CardProduct'
import TitleProduct from '../../atomics/Typography/TitleProduct'
import Button from '@material-ui/core/Button'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Collapse from '@material-ui/core/Collapse'
import ErrorIcon from '@material-ui/icons/Error'


const Container = styled('div')`
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
`
const CardVariant = styled(CardProduct)`
  padding-left: 0px;
  padding-right: 0px;
  padding-bottom: 0px;
  @media (max-width: 1100px) {
    width: 100%;
    height: 100%;
    padding: 0px;
  }
`

const Head = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding-left: 58px;
  padding-right: 58px;
  width: 100%;
  .MuiButtonBase-root:disabled {
    cursor: not-allowed;
    pointer-events: auto;
  }
  @media (max-width: 1100px) {
    padding-left: 3%;
    padding-right: 3%;
  }
`

const Text = styled('p')`
  font-size: 18px;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 0.20000000298023224px;
  text-align: left;
  margin: 0;
`

const ErrorText = styled('p')`
  color: red;
  font-size: 18px;
  font-style: italic;
  margin: 0px;
  margin-bottom: 3%;
  display: flex;
  align-items: center;
  gap: 5px;
`

const ButtonStyled = styled(Button)`
  color: #2469ce;
  font-size: 18px;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 0.20000000298023224px;
  text-align: left;
  text-decoration: underline;
  align-self: flex-end;
`

const SubContainer = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  padding-right: 58px;
  margin-bottom: 2%;
`
const ContainerVariantText = styled('div')`
  display: flex;
  flex-direction: row;
  width: 50%;
  padding-left: 72px;
`

const CheckboxStyle = styled(Checkbox)`
  margin-right: 5%;
`

const VariantText = styled(Text)`
  font-size: 18px;
  font-weight: 500;
  line-height: 21.09px;
  color: ${({ theme }) => theme.palette.text.primary};
`

const DeleteVariantListStyle = styled(Button)`
  font-size: 19px;
  font-weight: bold;
  line-height: 22px;
  text-decoration: underline;
  text-transform: capitalize;
  color: ${({ theme }) => theme.palette.secondary.main};
  padding: 0;
`

const ProductVariantForm = ({
  variants,
  control,
  errors
}) => {
  const { remove, append, replace } = useFieldArray({
    control,
    name: 'variants'
  })

  const variantsWatch = useWatch({ control, name: 'variants' })
  const mediaWatch = useWatch({ control, name: 'media' }) || []
  const safeMediaWatch = Array.isArray(mediaWatch) ? mediaWatch : []
  const [checked, setChecked] = useState(false)
  const [newVariants, setNewVariants] = useState([])
  useEffect(() => {
    setNewVariants(variants)
  }, [variants])

  // Sincronizar newVariants con variantsWatch cuando cambian los valores del formulario
  useEffect(() => {
    if (variantsWatch && variantsWatch.length > 0) {
      setNewVariants(prev => {
        // Mantenemos las propiedades de UI como 'checked' que no están en el formulario
        return variantsWatch.map((variant, index) => ({
          ...variant,
          checked: prev[index]?.checked || false,
          id: prev[index]?.id !== undefined ? prev[index].id : index
        }))
      })
    }
  }, [variantsWatch])

  const count = useMemo(
    () => Array(...newVariants).filter(({ checked }) => checked)?.length,
    [newVariants]
  )

  const handleAllChecked = () => {
    setChecked(!checked)
    setNewVariants(prev =>
      [...prev]?.map((variant, index) => ({
        ...variant,
        checked: index === 0 ? false : !checked
      }))
    )
  }

  const handleDelete = () => {
    const newList = newVariants.filter(({ checked }) => !checked)
    setNewVariants(newList)
    replace(newList)
  }

  const handleOnChange = event => {
    setNewVariants(prev =>
      prev.map(variant =>
        variant.id.toString() === event.target.name
          ? { ...variant, checked: event.target.checked }
          : variant
      )
    )
  }

  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState(0)
  const [isEdit, setEdit] = useState(true)


  const checkHasError = index => {
    if ((errors.variants || []).length >= index + 1) {
      return errors.variants[index] !== undefined
    }
    return false
  }

  const isVariant = !!variantsWatch
  const showTextError = (errors?.variants || []).length > 0
  return (
    <>
      <ModalTemplate
        index={selected}
        control={control}
        errors={errors}
        open={open}
        onClose={() => {
          if (variantsWatch.length > newVariants.length) {
            remove(-1)
          }
          setEdit(true)
          setOpen(false)
        }}
        onSubmit={() => {
          // react-hook-form maneja los cambios automáticamente
          // newVariants se sincroniza via useEffect con variantsWatch
          setEdit(true)
          setOpen(false)
        }}
        media={safeMediaWatch}
        title={isEdit ? 'Editar variante' : 'Agregar variante'}
        subtitle={
          isEdit
            ? 'Edita características correspondientes solamente a la variante.'
            : 'Agrega características correspondientes solamente a la variante.'
        }
      />
      <CardVariant>
        <Head>
          <ButtonStyled
            disabled={!isVariant}
            onClick={() => {
              // Obtener la primera imagen disponible del carousel
              const firstImageUrl =
                safeMediaWatch[0]?.preview?.image?.url ||
                safeMediaWatch[0]?.src ||
                ''

              append({
                color: '',
                compareAtPrice: variantsWatch[0]?.compareAtPrice,
                price: variantsWatch[0]?.price,
                weight: variantsWatch[0]?.weight,
                inventoryQuantity: 0,
                ownSku: '',
                mediaIds: firstImageUrl ? [firstImageUrl] : []
              })
              setSelected(variantsWatch?.length)
              setEdit(false)
              setOpen(true)
            }}
          >
            + Agregar variante
          </ButtonStyled>
          <TitleProduct>Variantes del producto</TitleProduct>
          <Text>
            Si tu producto tiene más colores, aparte del seleccionado,
            agrega su variante aquí.
          </Text>
          {showTextError && (
            <ErrorText>
              <ErrorIcon fontSize="15px" />
              Una o más de las variantes editadas presenta errores, por
              favor revísala(s).
            </ErrorText>
          )}
        </Head>
        {isVariant ? (
          <Container>
            <Collapse in={count > 0}>
              <SubContainer>
                <ContainerVariantText>
                  <FormControlLabel
                    control={
                      <CheckboxStyle
                        checked={count > 0}
                        indeterminate={count > 0}
                        onChange={handleAllChecked}
                      />
                    }
                  />
                  <VariantText>
                    {count === 1
                      ? `${count} variante seleccionada`
                      : `${count} variantes seleccionadas`}
                  </VariantText>
                </ContainerVariantText>
                <DeleteVariantListStyle onClick={handleDelete}>
                  Eliminar
                </DeleteVariantListStyle>
              </SubContainer>
            </Collapse>
            <VariantItem
              disabled
              variantId={0}
              variantName={isVariant ? variantsWatch[0]?.color : ''}
              variantPrice={isVariant ? variantsWatch[0]?.price : 0}
              hasError={checkHasError(0)}
              variantCompareAtPrice={
                isVariant ? variantsWatch[0]?.compareAtPrice : 0
              }
              variantAlt={isVariant ? variantsWatch[0]?.color : 'variant'}
              variantSku={isVariant ? variantsWatch[0]?.ownSku : ''}
              variantQuantity={
                isVariant ? variantsWatch[0]?.inventoryQuantity : 0
              }
              variantImage={
                isVariant && safeMediaWatch
                  ? (safeMediaWatch &&
                    safeMediaWatch.filter(({ alt }) =>
                      variantsWatch
                        ? alt?.includes(variantsWatch[0].color)
                        : false
                    )[0]?.preview?.image?.url) ||
                  (safeMediaWatch &&
                    safeMediaWatch.filter(({ alt }) =>
                      variantsWatch
                        ? alt?.includes(variantsWatch[0].color)
                        : false
                    )[0]?.src) ||
                  safeMediaWatch.find(({ preview }) =>
                    variantsWatch && variantsWatch[0].mediaIds
                      ? variantsWatch[0]?.mediaIds[0] ===
                      preview?.image?.url
                      : false
                  )?.preview?.image?.url ||
                  safeMediaWatch.find(({ preview }) =>
                    variantsWatch && variantsWatch[0].mediaIds
                      ? variantsWatch[0]?.mediaIds[0] === preview?.image?.url
                      : false
                  )?.src ||
                  safeMediaWatch[0]?.preview?.image?.url ||
                  safeMediaWatch[0]?.src
                  : ''
              }
              onChange={handleOnChange}
              checked={false}
              onClick={() => {
                setSelected(0)
                setOpen(true)
              }}
            />
            {newVariants
              ?.slice(1)
              ?.map((variant, index) => {
                const idMedaIds = isArray(variant?.mediaIds)
                  ? variant?.mediaIds[0]
                  : undefined
                const media = isArray(variant.media)
                  ? variant?.media[0]
                  : undefined

                return (
                  <VariantItem
                    key={variant?.color || String(index)}
                    variantId={variant?.id}
                    variantName={variant?.color}
                    variantPrice={variant?.price}
                    hasError={checkHasError(index + 1)}
                    variantCompareAtPrice={
                      isVariant
                        ? variantsWatch[0]?.compareAtPrice
                        : variant?.compareAtPrice
                    }
                    variantAlt={
                      media?.preview?.image?.altText
                        ? media?.preview?.image?.altText
                        : variant?.color
                    }
                    variantSku={variant?.ownSku}
                    variantQuantity={variant?.inventoryQuantity}
                    variantImage={
                      idMedaIds &&
                        idMedaIds.includes('https://cdn.shopify.com')
                        ? idMedaIds
                        : idMedaIds && safeMediaWatch
                          ? safeMediaWatch.find(
                            ({ preview }) =>
                              idMedaIds === preview?.image?.url
                          )?.preview?.image?.url ||
                          safeMediaWatch.find(
                            ({ src }) =>
                              idMedaIds === src
                          )?.src ||
                          safeMediaWatch[0]?.preview?.image?.url ||
                          safeMediaWatch[0]?.src
                          : safeMediaWatch[0]?.preview?.image?.url ||
                          safeMediaWatch[0]?.src ||
                          ''
                    }
                    onChange={handleOnChange}
                    checked={variant?.checked}
                    onClick={() => {
                      setSelected(index + 1)
                      setOpen(true)
                    }}
                  />
                )
              })}
          </Container>
        ) : null}
      </CardVariant>
    </>
  )
}

ProductVariantForm.propTypes = {
  errors: PropTypes.any,
  control: PropTypes.any,
  variants: PropTypes.array
}

ProductVariantForm.defaultProps = {
  variants: []
}
export default ProductVariantForm
